import { useState } from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";

function HomePage() {
  const whyThoughtCodesItems = [
    {
      title: "Mission-focused delivery",
      description:
        "Solutions aligned to agency goals, operational needs, and measurable outcomes.",
    },
    {
      title: "Security-first engineering",
      description:
        "Secure-by-design delivery across application, infrastructure, and operations.",
    },
    {
      title: "Modernization expertise",
      description:
        "Support for transitions from legacy systems to high-performing digital environments.",
    },
    {
      title: "Responsive teaming",
      description:
        "Flexible collaboration with customers and partners in evolving delivery environments.",
    },
  ];
  const [activeWhyThoughtCodesIndex, setActiveWhyThoughtCodesIndex] = useState(0);

  return (
    <div className="min-h-screen bg-[#fff] text-black">
      <Navbar />
      <main>
        <section id="home" className="relative hero">
          <div className="container hero-grid">
            <div>
              <span className="eyebrow">
                IT Modernization | Software | Cloud | AI
              </span>
              <h1 className="font-bold">
                Transforming legacy systems into secure, scalable digital
                solutions
              </h1>
              <p>
                ThoughtCodes helps mission-driven organizations modernize
                operations, deliver better user experiences, strengthen
                security, and accelerate outcomes through software engineering,
                Agile delivery, cloud, DevSecOps, cybersecurity, and AI-enabled
                automation.
              </p>
              <div className="hero-actions">
                <a href="/services" className="btn light cta-button">
                  Explore Services
                </a>
                <a
                  href="/contact"
                  className="hero-outline-btn rounded-xl border border-white/30 px-6 py-3 text-base font-medium text-white"
                >
                  Talk to ThoughtCodes
                </a>
              </div>
            </div>
            <div className="hero-side">
              <div className="hero-card stat stat-interactive">
                <div className="text-black font-bold text-xl mb-4">
                  What we deliver
                </div>
                <ul className="text-black list-disc list-inside">
                  <li>Custom software and application development</li>
                  <li>Agile digital transformation</li>
                  <li>Cloud and DevSecOps enablement</li>
                  <li>AI-enabled automation solutions</li>
                  <li>Systems modernization and quality engineering</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        <section className="section why-thought-codes-section ">
          <div className="container">
            <div className="section-head center">
              <div className="tag mb-4">Why ThoughtCodes</div>
              <div className="text-black font-bold text-2xl md:text-4xl mb-4">
                Small business agility with enterprise discipline
              </div>
              <p>
                We combine technical excellence with a deep understanding of
                federal mission environments to deliver solutions that are not
                only innovative but also practical, secure, and sustainable. Our
                collaborative approach ensures alignment with stakeholder goals
                while maintaining speed, quality, and compliance.
              </p>
            </div>
            <div className="kpi">
              {whyThoughtCodesItems.map((item, index) => {
                const isActive = activeWhyThoughtCodesIndex === index;
                return (
                  <button
                    key={item.title}
                    type="button"
                    onClick={() => setActiveWhyThoughtCodesIndex(index)}
                    onFocus={() => setActiveWhyThoughtCodesIndex(index)}
                    className={`stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)] ${isActive ? "" : ""}`}
                    aria-pressed={isActive}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
        <section className="section soft core-services-section gradient-bg">
          <div className="container">
            <div className="section-head center">
              <div className="tag mb-4">Core Services</div>
              <div className="text-black font-bold text-2xl md:text-4xl mb-4">
                Capabilities built for federal and mission-driven environments
              </div>
              <p>
                Web-ready content mapped to a services-style template and
                optimized for government contracting search intent.
              </p>
            </div>
            <div className="grid-3">
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">01</div>
                <h3>Software Development</h3>
                <p>
                  Secure, scalable web, mobile, API, and enterprise solutions
                  built for performance and usability.
                </p>
              </div>
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">02</div>
                <h3>Cloud & DevSecOps</h3>
                <p>
                  Cloud migration, CI/CD, automation, and DevSecOps pipelines
                  that improve resilience and delivery speed.
                </p>
              </div>
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">03</div>
                <h3>Cybersecurity & Compliance</h3>
                <p>
                  Threat-informed engineering, vulnerability management, and
                  compliance-aligned implementation.
                </p>
              </div>
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">04</div>
                <h3>Agile Transformation</h3>
                <p>
                  Agile delivery models, coaching, and product-focused execution
                  that improve speed and collaboration.
                </p>
              </div>
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">05</div>
                <h3>AI Automation</h3>
                <p>
                  Intelligent automation and data-driven workflows that reduce
                  manual effort and improve decision-making.
                </p>
              </div>
              <div className="service stat stat-interactive !shadow-[0_12px_32px_rgba(2,6,23,0.12)]">
                <div className="icon">06</div>
                <h3>Systems Modernization</h3>
                <p>
                  Legacy assessment, reengineering, integration, and
                  modernization support for long-term mission value.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container split">
            <div>
              <div className="section-head">
                <div className="tag mb-4">Relevant Experience</div>
                <div className="text-black font-bold text-2xl md:text-4xl mb-4">
                  Impact across government programs
                </div>
              </div>
              <div className="table-like">
                <div className="row-item stat stat-interactive">
                  <strong>DHS/ICE</strong>
                  <div>
                    Delivered a dynamic HR workflow system that streamlined
                    administrative tasks and improved user experience.
                  </div>
                </div>
                <div className="row-item stat stat-interactive">
                  <strong>DOD/DLA</strong>
                  <div>
                    Supported design and modernization efforts using DoDAF and
                    government architecture practices to help meet federal
                    compliance requirements.
                  </div>
                </div>
                <div className="row-item stat stat-interactive">
                  <strong>Department of Labor</strong>
                  <div>
                    Enabled access to diverse datasets through API-driven
                    services supporting formats such as SDK, XML, and JSON.
                  </div>
                </div>
                <div className="row-item stat stat-interactive">
                  <strong>USAID</strong>
                  <div>
                    Supported humanitarian response innovation through
                    development and testing of the SwitchMobile application.
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="section-head">
                <div className="tag mb-4 min-h-[21px]"></div>
                <div className="text-black font-bold text-2xl md:text-4xl mb-4">
                  Included in this package
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="card">
                  <h3>Focused on Federal Technology Priorities</h3>
                  <p>
                    Our services align with the core technology needs of federal
                    agencies and mission-driven programs, including
                    modernization, secure software development, cloud
                    transformation, and AI-enabled innovation.
                  </p>
                  <ul className="list-check">
                    <li>Government IT modernization</li>
                    <li>Secure federal software development</li>
                    <li>Cloud migration and DevSecOps enablement</li>
                    <li>Cybersecurity and compliance (NIST, FedRAMP, FISMA)</li>
                    <li>Systems integration and modernization</li>
                    <li>AI and automation for mission operations</li>
                  </ul>
                </div>
                <div className="card">
                  <h3>Comprehensive Digital Solutions</h3>
                  <p>
                    We provide end-to-end support across the full digital
                    lifecycle - from strategy and design to development,
                    deployment, and continuous improvement.
                  </p>
                  <ul className="list-check">
                    <li>Mission-driven solution design and delivery</li>
                    <li>Application development and modernization</li>
                    <li>Cloud and infrastructure engineering</li>
                    <li>DevSecOps and automation implementation</li>
                    <li>Quality engineering and testing</li>
                    <li>Ongoing support and optimization</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="cta">
              <h2>Ready to modernize with ThoughtCodes?</h2>
              <p>
                Partner with a secure, agile, and mission-focused technology
                team for software development, modernization, cloud, DevSecOps,
                cybersecurity, and AI-enabled automation initiatives.
              </p>
              <a className="btn light mt-4 cta-button" href="/contact">
                Contact ThoughtCodes
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default HomePage;
